import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  GrrsEU,
  GrrsNoneEuCountries,
  GrrsSiteCountry,
  GRRS_EU_COUNTRIES,
  GRRS_NON_EU_COUNTRIES,
} from "../../data/global-rights-countries";
import { ApplicationState } from "../../store";
import { UserState } from "../../store/reducers/user-reducer";
import { Container } from "../Grid/Grid";
import { Svg, SVG_ICONS } from "../__common/Svg/Svg";
import { Typography } from "../__common/Typography/Typography";
import "./GlobalSiteLinkPopup.scss";
import { useCookie } from "../../hooks/cookie-hooks";
import {
  EVENT_CATEGORY_GPS_POPUP,
  EVENT_GPS_ACTION_LINK_VISIT,
  EVENT_GPS_ACTION_COUNTRY_VISIT,
} from "../../constants/analytics";
import { UtilsUser } from "../../utils/UtilsUser";
import GoogleAnalytics from "../../utils/GoogleAnalytics";
import classNames from "classnames";

type GlobalSiteContext = "default" | "mydata" | "global-rights";

interface Props {
  onHide: () => void;
  testId?: string;
}
/**
 * Resolves the GRRS site country data based on the user's country code.
 * Returns the country data if found (EU or non-EU), or null otherwise.
 */
const resolveGrrsCountry = (countryCode: string): GrrsSiteCountry | null => {
  if (
    Object.values(GRRS_NON_EU_COUNTRIES).includes(
      countryCode as GRRS_NON_EU_COUNTRIES
    )
  ) {
    return GrrsNoneEuCountries[countryCode as GRRS_NON_EU_COUNTRIES];
  }

  if (
    Object.values(GRRS_EU_COUNTRIES).includes(countryCode as GRRS_EU_COUNTRIES)
  ) {
    return GrrsEU;
  }

  return null;
};

export const GlobalSiteLinkPopup: React.FC<Props> = ({ onHide, testId }) => {
  const { t } = useTranslation();
  const { isShowMemberNotice, setCookieShowMemberNotice } = useCookie();
  const { userData } = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );
  const [grrsCountryData, setGrrsCountryData] =
    useState<GrrsSiteCountry | null>(null);

  const handleOnHideClick = useCallback(() => {
    onHide();
    setCookieShowMemberNotice();
  }, [onHide, setCookieShowMemberNotice]);

  const triggerCountryVisitEvent = useCallback((country: string) => {
    GoogleAnalytics.triggerEvent(
      EVENT_CATEGORY_GPS_POPUP,
      `${EVENT_GPS_ACTION_COUNTRY_VISIT}-${country}`
    );
  }, []);

  const triggerLinkVisitEvent = useCallback((country: string) => {
    GoogleAnalytics.triggerEvent(
      EVENT_CATEGORY_GPS_POPUP,
      `${EVENT_GPS_ACTION_LINK_VISIT}-${country}`
    );
  }, []);

  useEffect(() => {
    const countryData = resolveGrrsCountry(userData.countryCode);
    if (countryData) {
      triggerCountryVisitEvent(userData.countryCode);
      setGrrsCountryData(countryData);
    }
  }, [userData.countryCode, triggerCountryVisitEvent]);

  // Early return: don't render if notice is dismissed or user is email-logged-in
  if (!isShowMemberNotice || UtilsUser.isEmailLogin()) {
    return null;
  }

  // Early return: don't render if no country data resolved
  if (!grrsCountryData) {
    return null;
  }

  return (
    <Container>
      <div className="global-site__container">
        <Svg
          icon={SVG_ICONS.MAP_PIN}
          width={24}
          height={24}
          title={t("PRIVACY_GRRS_NOTICE_COUNTRY", {
            "%s": grrsCountryData.name,
          })}
        />
        <Typography
          variant="body1"
          component="span"
          className="global-site__container__info"
          weight="w500"
        >
          {t("PRIVACY_GRRS_NOTICE_COUNTRY", {
            "%s": grrsCountryData.name,
          })}
        </Typography>
        <a
          onClick={() => triggerLinkVisitEvent(grrsCountryData.code)}
          href={grrsCountryData.link}
          rel="noopener noreferrer"
          target="_blank"
          className="global-site__container__info__link"
        >
          {t("PRIVACY_GRRS_NOTICE_TEXT", {
            "%s": grrsCountryData.desc ?? grrsCountryData.name,
          })}
        </a>
        <button
          data-testid={testId}
          onClick={handleOnHideClick}
          className="global-site__container__close"
        >
          <Svg icon={SVG_ICONS.CLOSE_SMALL} width={16} height={16} />
        </button>
      </div>
    </Container>
  );
};
